// client/src/pages/Auth/Register.js
// Registreringssida. Email/password + GDPR-samtycke mot AuthContext.register().
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Auth.css'; // Shared styling with Login

const Register = () => {
  // Auth context and navigation
  const { register, isAuthenticated, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    gdprConsent: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    setFormErrors({});
  }, [formData, clearError, error]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Password strength checker
  const getPasswordStrength = (password) => {
    if (password.length < 8) return { strength: 'weak', message: 'Minst 8 tecken' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;
    
    if (score < 3) return { strength: 'weak', message: 'Svagt lösenord' };
    if (score < 4) return { strength: 'medium', message: 'Medel styrka' };
    return { strength: 'strong', message: 'Starkt lösenord' };
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = 'Email är obligatorisk';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Ange en giltig email-adress';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Lösenord är obligatoriskt';
    } else if (formData.password.length < 8) {
      errors.password = 'Lösenord måste vara minst 8 tecken';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Lösenord måste innehålla stor bokstav, liten bokstav och siffra';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Bekräfta lösenord';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Lösenorden matchar inte';
    }

    // GDPR consent validation
    if (!formData.gdprConsent) {
      errors.gdprConsent = 'Du måste acceptera villkoren';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await register({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        gdprConsent: formData.gdprConsent
      });

      if (result.success) {
        console.log('✅ Registration successful');
        setRegistrationSuccess(true);
        // Don't redirect immediately - show success message first
      }
    } catch (registrationError) {
      console.error('❌ Registration failed:', registrationError.message);
      // Error is handled by AuthContext and displayed via error state
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-fill demo data
  const handleDemoData = () => {
    setFormData({
      email: 'demo.user@example.com',
      password: 'DemoPass123!',
      confirmPassword: 'DemoPass123!',
      gdprConsent: true
    });
  };

  // If registration was successful, show success message
  if (registrationSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-wrapper">
          <div className="auth-card">
            <div className="auth-header">
              <div className="auth-logo">
                <span role="img" aria-label="Success">✅</span>
                <h1>Registrering lyckades!</h1>
              </div>
              <h2>Kontrollera din email</h2>
              <p>
                Vi har skickat en verifieringslänk till <strong>{formData.email}</strong>. 
                Klicka på länken i emailet för att aktivera ditt konto.
              </p>
            </div>

            <div className="success-actions">
              <Link to="/login" className="auth-submit-btn">
                🔑 Gå till inloggning
              </Link>
              
              <div className="auth-footer">
                <p>
                  Ser du inte emailet? Kolla spam-mappen eller{' '}
                  <button 
                    className="resend-link"
                    onClick={() => setRegistrationSuccess(false)}
                  >
                    försök igen
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        
        {/* Auth Card */}
        <div className="auth-card">
          
          {/* Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <span role="img" aria-label="Health">🏥</span>
              <h1>Health Data System</h1>
            </div>
            <h2>Skapa ditt konto</h2>
            <p>Gå med i Health Data System för personliga hälsoinsikter och community support.</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="auth-error">
              <span className="error-icon">❌</span>
              <span className="error-message">{error}</span>
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="auth-form">
            
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                📧 Email-adress
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${formErrors.email ? 'error' : ''}`}
                placeholder="din.email@example.com"
                autoComplete="email"
                disabled={loading || isSubmitting}
              />
              {formErrors.email && (
                <span className="form-error">{formErrors.email}</span>
              )}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                🔐 Lösenord
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.password ? 'error' : ''}`}
                  placeholder="Skapa ett starkt lösenord"
                  autoComplete="new-password"
                  disabled={loading || isSubmitting}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading || isSubmitting}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {formData.password && (
                <div className={`password-strength ${getPasswordStrength(formData.password).strength}`}>
                  <div className="strength-bar"></div>
                  <span className="strength-text">
                    {getPasswordStrength(formData.password).message}
                  </span>
                </div>
              )}
              
              {formErrors.password && (
                <span className="form-error">{formErrors.password}</span>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                🔐 Bekräfta lösenord
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.confirmPassword ? 'error' : ''}`}
                  placeholder="Ange lösenordet igen"
                  autoComplete="new-password"
                  disabled={loading || isSubmitting}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading || isSubmitting}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <span className="form-error">{formErrors.confirmPassword}</span>
              )}
            </div>

            {/* GDPR Consent */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="gdprConsent"
                  checked={formData.gdprConsent}
                  onChange={handleInputChange}
                  className="checkbox-input"
                  disabled={loading || isSubmitting}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">
                  Jag accepterar{' '}
                  <Link to="/terms" className="terms-link">användarvillkoren</Link>
                  {' '}och{' '}
                  <Link to="/privacy" className="terms-link">integritetspolicyn</Link>
                  {' '}(GDPR-kompatibel)
                </span>
              </label>
              {formErrors.gdprConsent && (
                <span className="form-error">{formErrors.gdprConsent}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`auth-submit-btn ${isSubmitting || loading ? 'loading' : ''}`}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  Skapar konto...
                </>
              ) : (
                <>
                  ✨ Skapa konto
                </>
              )}
            </button>

          </form>

          {/* Demo Section */}
          <div className="demo-section">
            <div className="divider">
              <span>eller</span>
            </div>
            <button
              type="button"
              className="demo-btn"
              onClick={handleDemoData}
              disabled={loading || isSubmitting}
            >
              🎯 Fyll i demo-uppgifter
            </button>
          </div>

          {/* Login Link */}
          <div className="auth-footer">
            <p>
              Har du redan ett konto?{' '}
              <Link to="/login" className="auth-link">
                Logga in här
              </Link>
            </p>
          </div>

        </div>

        {/* Side Info */}
        <div className="auth-info">
          <h3>🚀 Dina fördelar</h3>
          <ul>
            <li>✅ Personliga hälsoinsikter</li>
            <li>✅ Symptomprognos & återhämtningstid</li>
            <li>✅ Community support</li>
            <li>✅ Säker datahantering</li>
          </ul>
          
          <div className="features-preview">
            <h4>Tillgång till:</h4>
            <ul>
              <li>📊 Avancerad hälsoanalys</li>
              <li>🔮 AI-baserade prognoser</li>
              <li>💬 Community chat</li>
              <li>📈 Personlig utveckling</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Register;